import {
  Ban,
  CheckCircle2,
  CircleDot,
  Clock,
  Loader2,
  Octagon,
  RotateCcw,
  TriangleAlert,
  XCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, type BadgeTone } from './Badge';
// Bewusst kein RunStatus-Import - wir akzeptieren string und mappen defensiv.

/**
 * RunStatusBadge - eigenes Tone+Icon pro Run-Status (Sprint 5, WT-506).
 *
 * Wir akzeptieren auch zusätzliche "logische" Zustände, die das Backend an
 * gefiltrierte Lifecycle-Phasen weiterreicht (z.B. preparing). Mapping ist
 * defensiv: unbekannte Status fallen auf neutral zurück.
 */

/** Bekannte UI-Status für den Badge - tatsächlicher Status kann auch andere Strings sein. */
export type KnownRunStatus =
  | 'pending'
  | 'queued'
  | 'preparing'
  | 'running'
  | 'success'
  | 'partial'
  | 'failed'
  | 'blocked'
  | 'rolled_back'
  | 'cancelled'
  | 'aborted';

interface BadgeConfig {
  label: string;
  tone: BadgeTone;
  icon: ReactNode;
  /** Kennzeichnet "in flight" - UI kann darauf basierend pulse-Animation zeigen. */
  pulsing?: boolean;
  /** Tooltip am Badge - dort steht, was das Label nicht in drei Worte kriegt. */
  title?: string;
}

const STATUS_CONFIG: Record<string, BadgeConfig> = {
  pending: {
    label: 'queued',
    tone: 'neutral',
    icon: <Clock className="size-3" aria-hidden="true" />,
  },
  queued: {
    label: 'queued',
    tone: 'neutral',
    icon: <Clock className="size-3" aria-hidden="true" />,
  },
  preparing: {
    label: 'vorbereitet',
    tone: 'primary',
    icon: <Loader2 className="size-3 animate-spin" aria-hidden="true" />,
    pulsing: true,
  },
  running: {
    label: 'läuft',
    tone: 'primary',
    icon: <Loader2 className="size-3 animate-spin" aria-hidden="true" />,
    pulsing: true,
  },
  success: {
    label: 'erfolgreich',
    tone: 'success',
    icon: <CheckCircle2 className="size-3" aria-hidden="true" />,
  },
  partial: {
    label: 'partiell',
    tone: 'warning',
    icon: <TriangleAlert className="size-3" aria-hidden="true" />,
  },
  failed: {
    label: 'fehlgeschlagen',
    tone: 'danger',
    icon: <XCircle className="size-3" aria-hidden="true" />,
  },
  // Vorfall 13.08.2026 (Tischlerei Koeck): zwei Läufe standen rot als
  // "fehlgeschlagen" da, obwohl der Client nie auf die Kisten kam - kein
  // Schlüssel, keine Bitwarden-Sitzung. Ein Lauf, der NIE STATTGEFUNDEN hat,
  // darf nicht aussehen wie eine Wartung, die am Kundensystem gescheitert ist.
  // Darum gelb statt rot (gleiche Sprache wie failureTone: gelb = "wir sind
  // gar nicht drangekommen") und ein Label, das die Konsequenz nennt statt
  // nur den Zustandsnamen zu wiederholen.
  blocked: {
    label: 'blockiert – nichts ausgeführt',
    tone: 'warning',
    icon: <Ban className="size-3" aria-hidden="true" />,
    title:
      'Kein Zugang zu den Zielsystemen: der Lauf hat nie stattgefunden. ' +
      'Am Kundensystem wurde nichts verändert - sobald der Zugang steht, ' +
      'ist der Lauf unverändert wiederholbar.',
  },
  rolled_back: {
    label: 'rolled-back',
    tone: 'warning',
    icon: <RotateCcw className="size-3" aria-hidden="true" />,
  },
  cancelled: {
    label: 'abgebrochen',
    tone: 'neutral',
    icon: <Octagon className="size-3" aria-hidden="true" />,
  },
  aborted: {
    label: 'abgebrochen',
    tone: 'neutral',
    icon: <Octagon className="size-3" aria-hidden="true" />,
  },
};

/**
 * Deutsches Label eines Run-Status ohne Badge-Drumherum - für Filter-Listen
 * und Auswahlfelder, damit dort nicht der Rohwert neben dem Badge steht.
 * Unbekannte Status kommen roh zurück.
 */
export function runStatusLabel(status: string): string {
  return STATUS_CONFIG[status]?.label ?? status;
}

interface Props {
  /** Run-Status; Backend kann auch unbekannte Strings senden (fällt auf Raw zurück). */
  status: string;
  className?: string;
  'data-testid'?: string;
}

export function RunStatusBadge({ status, className, 'data-testid': testId }: Props): JSX.Element {
  const cfg: BadgeConfig = STATUS_CONFIG[status] ?? {
    label: status,
    tone: 'neutral',
    icon: <CircleDot className="size-3" aria-hidden="true" />,
  };

  const badgeProps: { tone: BadgeTone; className?: string; 'data-testid'?: string } = {
    tone: cfg.tone,
  };
  if (className !== undefined) badgeProps.className = className;
  if (testId !== undefined) badgeProps['data-testid'] = testId;

  return (
    <Badge {...badgeProps}>
      <span
        className="inline-flex items-center gap-1"
        {...(cfg.title !== undefined ? { title: cfg.title } : {})}
      >
        {cfg.icon}
        <span>{cfg.label}</span>
      </span>
    </Badge>
  );
}
